import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import CustomDatePicker from '../../components/filter/CustomDatePicker';
import APPCONSTANTS from '../../constants/appConstants';
import DetailCard from '../../components/detailCard/DetailCard';
import CustomTable from '../../components/customtable/CustomTable';
import Loader from '../../components/loader/Loader';
import styles from './ReportList.module.scss';
import Modal from '../../components/modal/ModalForm';
import SpreadsheetViewer from '../../components/spreadSheetViewer/SpreadSheetViewer';
import { fetchReportList, fileDownload, fetchHFReport } from '../../services/reportAPI';
import {
  getPageLocationWithParams,
  reportNameFormat,
  trackGoogleAnalyticsEvent,
  reportSizeFormat
} from '../../utils/commonUtils';
import toastCenter from '../../utils/toastCenter';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector } from 'react-redux';
import { firstNameSelector, lastNameSelector, roleSelector } from '../../store/user/selectors';
import { getSiteDetailSelector } from '../../store/site/selectors';
import { PROTECTED_ROUTES } from '../../constants/route';

interface IMatchParams {
  regionId: string;
  regionName: string;
  siteId: string;
  siteName: string;
  reportType: string;
  monthYear: string;
}

interface IDatesParams {
  id?: number;
  year?: string;
  month?: string;
  label?: string;
}

interface IReportLists {
  id: number;
  key: string;
}

export const formatMonthName = (month: string): string => {
  const result = APPCONSTANTS.MONTH.find((item) => item?.label === month);
  return result?.name || '';
};

const HFReport = (props: IMatchParams): React.ReactElement => {
  const history = useHistory();
  const { regionId, regionName, siteId, siteName, reportType } = useParams<IMatchParams>();
  const reportName: string = reportType.slice(0, 3);
  const startYearPickerRef = useRef<DatePicker>(null);
  const startMonthPickerRef = useRef<DatePicker>(null);
  const [modalParams, setModalReqParams] = useState({
    loading: false,
    data: {}
  });
  const [loading, setLoading] = useState(false);
  const [reportParams, setReportParams] = useState<IDatesParams[]>([]);
  const [startYear, setStartYear] = useState<null | Date>(null);
  const [startMonth, setStartMonth] = useState<null | Date>(null);
  const role: any = useSelector(roleSelector);
  const firstName: string = useSelector(firstNameSelector) || '';
  const lastName: string = useSelector(lastNameSelector) || '';
  const siteDetails = useSelector(getSiteDetailSelector);
  let selectedYear: string;
  let selectedMonth: string;

  useEffect(() => {
    getReportList('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    trackGoogleAnalyticsEvent(
      APPCONSTANTS.USER_ACTIONS.LIST_VIEW,
      reportNameFormat(reportType),
      getPageLocationWithParams(),
      { username: `${firstName} ${lastName}`, role: role.label }
    );
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!startYear) {
      startMonthPickerRef.current?.props.onChange(null, undefined);
      setStartMonth(null);
    }
    if ((startYear && !startMonth) || (startYear && startMonth)) {
      startMonthPickerRef.current?.props.minDate?.setDate(startYear.getDate());
      startMonthPickerRef.current?.props.minDate?.setMonth(startYear.getMonth());
      startMonthPickerRef.current?.props.minDate?.setFullYear(startYear.getFullYear());
    }
    if (startMonth) {
      startYearPickerRef.current?.props.maxDate?.setDate(startMonth.getDate());
      startYearPickerRef.current?.props.maxDate?.setMonth(startMonth.getMonth());
      startYearPickerRef.current?.props.maxDate?.setFullYear(startMonth.getFullYear());
    }
  }, [startYear, startMonth]);

  const isUserView = reportNameFormat(reportType) === 'HF4-(USER VIEW)';

  const validMonths = (year: string) => {
    if (year === '2025') {
      const currentMonth = new Date().getMonth() + 1;

      return APPCONSTANTS.MONTH.filter((item) => item.id >= 2 && item.id < currentMonth);
    }
    return APPCONSTANTS.MONTH;
  };

  const getReportList = useCallback(
    async (yearMonth: string) => {
      const datesList: IDatesParams[] = [];
      try {
        setLoading(true);
        let payLoad = { data: { entityList: [] } };
        if ([APPCONSTANTS.ROLES.PHU_INCHARGE, APPCONSTANTS.ROLES.HF4_REPORT_USER].includes(role) && isUserView) {
          const year =
            yearMonth && yearMonth.length > 4
              ? yearMonth.slice(3, 7)
              : yearMonth || new Date().getFullYear().toString();
          const month = yearMonth && yearMonth.length > 4 ? yearMonth.slice(0, 3) : '';
          validMonths(year).forEach((item) => {
            if (!!month ? month === item.label : true) {
              datesList.push({
                id: item.id,
                year,
                label: item.label,
                month: item.name
              });
            }
          });
        } else {
          payLoad = await fetchReportList({
            key: yearMonth,
            reportType: reportType.slice(0, 3).toUpperCase(),
            healthFacilityId: Number(siteId),
            limit: APPCONSTANTS.NUMBER.TWELVE,
            userId: siteDetails?.userId || undefined,
            userBased: !!siteDetails?.userId
          });
        }
        payLoad.data.entityList.map((res: IReportLists) => {
          return datesList.push({
            id: res.id,
            year: res.key.slice(3, 7),
            month: formatMonthName(res.key.slice(0, 3)),
            label: res.key.slice(0, 3)
          });
        });
        setLoading(false);
        setReportParams(datesList);
      } catch (error: any) {
        setReportParams(datesList);
        setLoading(false);
        toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.REPORT_NOT_FOUND);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportType, siteId]
  );

  const fetchReport = async (report: { label: any; year: any }) => {
    const {
      data: { entityList }
    } = await fetchHFReport({
      reportType: reportName.toUpperCase(),
      healthFacilityId: !siteDetails?.userId ? Number(siteId) : undefined,
      userId: siteDetails?.userId ? siteDetails?.userId : undefined,
      userBased: !!siteDetails?.userId,
      month: report.label,
      year: report.year
    });
    return entityList;
  };

  const getReportView = useCallback(
    async (report) => {
      try {
        setLoading(true);
        const entityList = await fetchReport(report);
        trackGoogleAnalyticsEvent(
          APPCONSTANTS.USER_ACTIONS.REPORT_VIEW,
          reportNameFormat(reportType),
          getPageLocationWithParams(),
          { username: `${firstName} ${lastName}`, role: role.label }
        );
        setLoading(false);
        setModalReqParams({ loading: true, data: entityList });
      } catch (error: any) {
        setLoading(false);
        toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.REPORT_NOT_FOUND_MONTH);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstName, lastName, role.label, siteId]
  );

  const downloadFile = useCallback(
    async (report) => {
      try {
        setLoading(true);
        const entityList = await fetchReport(report);
        trackGoogleAnalyticsEvent(
          APPCONSTANTS.USER_ACTIONS.REPORT_DOWNLOAD,
          reportNameFormat(reportType),
          getPageLocationWithParams(),
          { username: `${firstName} ${lastName}`, role: role.label }
        );
        setLoading(false);
        fileDownload(siteName, `_${reportName.toUpperCase()}_`, report.year + '_' + report.month, entityList);
        toastCenter.success(APPCONSTANTS.SUCCESS, APPCONSTANTS.REPORT_DOWNLOAD_SUCCESS);
      } catch (error: any) {
        setLoading(false);
        toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.REPORT_NOT_FOUND_MONTH);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [firstName, lastName, role.label, siteId, siteName]
  );

  const viewModalRender = () => {
    return (
      <SpreadsheetViewer
        data={modalParams.data}
        isEdit={false}
        className={styles.spreadSheetContainer}
        reportName={reportNameFormat(reportType).slice(0, 3)}
        colWidth={150}
        reportSize={reportSizeFormat(reportNameFormat(reportType))}
      />
    );
  };

  const handleCancelClick = () => {
    setModalReqParams({ loading: false, data: {} });
  };

  const getCHWList = (data: any) => {
    history.push(
      PROTECTED_ROUTES.chwList
        .replace(':regionId', regionId)
        .replace(':regionName', regionName)
        .replace(':siteId', siteId)
        .replace(':siteName', siteName.replace(/\//g, ''))
        .replace(':reportType', reportType)
        .replace(':monthYear', (data.label || '') + data.year)
    );
  };

  const onYearSelection = (date: Date) => {
    if (date) {
      trackGoogleAnalyticsEvent(
        APPCONSTANTS.USER_ACTIONS.FILTERS.YEAR_FILTER,
        APPCONSTANTS.REPORT_LIST.HF4_REPORT,
        getPageLocationWithParams(),
        { username: `${firstName} ${lastName}`, role: role.label }
      );
      setStartYear(date);
      selectedYear = date.getFullYear().toString();
      if (startMonth) {
        selectedMonth = APPCONSTANTS.MONTH[startMonth.getMonth()].label;
        getReportList(selectedMonth + selectedYear);
      } else {
        getReportList(selectedYear);
      }
    } else {
      setStartYear(null);
      setStartMonth(null);
      getReportList('');
    }
  };

  const onMonthSelection = (date: Date) => {
    if (date) {
      trackGoogleAnalyticsEvent(
        APPCONSTANTS.USER_ACTIONS.FILTERS.MONTH_FILTER,
        APPCONSTANTS.REPORT_LIST.HF4_REPORT,
        getPageLocationWithParams(),
        { username: `${firstName} ${lastName}`, role: role.label }
      );
      setStartMonth(date);
      selectedMonth = APPCONSTANTS.MONTH[date.getMonth()].label;
      if (startYear) {
        selectedYear = startYear.getFullYear().toString();
        getReportList(selectedMonth + selectedYear);
      }
    } else {
      setStartMonth(null);
      if (startYear) {
        selectedYear = startYear.getFullYear().toString();
        getReportList(selectedYear);
      }
    }
  };
  return (
    <>
      {loading && <Loader />}
      <div className='row'>
        <div className='col-auto'>
          <div className={`${styles.sideMenu} ${styles.customSidemenu}`}>
            <div className={`card-header bg-transparent ${styles.header}`}>
              <span className={styles.headerLabel}>Filters</span>
            </div>
            <br />
            <div>
              <label>Year</label>
              <CustomDatePicker
                pickerRef={startYearPickerRef}
                dayReducer={1}
                onDateSelect={(date: Date) => onYearSelection(date)}
                showYearPicker={true}
                minYear={isUserView ? '2025' : undefined}
              />
              <br />
              <br />
              <div className={startYear ? '' : `${styles.disable}`}>
                <label>Month</label>
                <CustomDatePicker
                  pickerRef={startMonthPickerRef}
                  onDateSelect={(date: Date) => onMonthSelection(date)}
                  showMonthYearPicker={true}
                />
              </div>
            </div>
          </div>
        </div>
        <div className='col'>
          <DetailCard header={reportNameFormat(reportType)}>
            <CustomTable
              rowData={reportParams}
              columnsDef={[
                {
                  id: 1,
                  name: 'year',
                  label: 'Year',
                  class: 'numeric'
                },
                {
                  id: 2,
                  name: 'month',
                  label: 'Month'
                }
              ]}
              isDelete={false}
              isEdit={false}
              isView={!isUserView}
              isDownload={!isUserView}
              isRowEdit={isUserView}
              handleRowClick={isUserView ? getCHWList : undefined}
              onRowView={getReportView}
              onRowDownload={downloadFile}
            />
          </DetailCard>
          <Modal
            show={modalParams.loading}
            title={`${reportNameFormat(reportType)} View`}
            size='modal-full'
            hasFooter={false}
            handleCancel={handleCancelClick}
            handleFormSubmit={handleCancelClick}
            render={viewModalRender}
          />
        </div>
      </div>
    </>
  );
};

export default HFReport;
